import { ExternalLinkIcon, formatAsCurrency, Link } from '@la/ds-ui-components';
import { useConsolidatedCheckout } from 'lib/context/ConsolidatedCheckoutContext/ConsolidatedCheckoutContext';
import { getHotelLinks } from 'redux/checkoutSlice';
import { useAppSelector } from 'redux/store';
import {
  BillingSummaryItemLineItem,
  CartItemHotelLink,
  CombinedCartItemData,
  DiscountCode,
} from '../Checkout.types';
import styles from './CheckoutBillingSummary.module.scss';
import { CheckoutBillingSummaryFees } from './CheckoutBillingSummaryFees/CheckoutBillingSummaryFees';

export type CheckoutBillingSummaryProps = {
  showHotelLinks?: boolean;
};

export const DUE_TODAY = 'Due today:';

/* CheckoutBillingSummary */
export default function CheckoutBillingSummary({
  showHotelLinks = false,
}: CheckoutBillingSummaryProps) {
  const {
    cartItems,
    taxAmount = '',
    transactionFees,
  } = useConsolidatedCheckout();

  const hotelLinks = useAppSelector(getHotelLinks);

  const hasTaxAmountsOrTransactionFees =
    Number.parseFloat(taxAmount) || Number.parseFloat(transactionFees);

  const hasPaymentOption = cartItems?.some(
    (cartItem) => cartItem.paymentTerm !== 'FULL'
  );

  return (
    <div className={styles.checkoutBillingSummary}>
      <CheckoutBillingSummaryDetail
        hotelLinks={showHotelLinks && hotelLinks.length > 1 ? hotelLinks : []}
      />
      <CheckoutBillingSummaryFees />
      {hasTaxAmountsOrTransactionFees ? (
        <hr className={styles.blueBorder} />
      ) : null}
      <CheckoutBillingSummaryHeader hasPaymentOption={hasPaymentOption} />
    </div>
  );
}
/* */

/*CheckoutBillingSummaryHeader */
function CheckoutBillingSummaryHeader({
  hasPaymentOption,
}: {
  hasPaymentOption?: boolean;
}) {
  const { dueToday, totalAmount } = useConsolidatedCheckout();
  const rowLabel = hasPaymentOption ? DUE_TODAY : 'Total due:';

  return (
    <div className={styles.checkoutBillingSummaryHeader}>
      <div className={styles.dueTodayRow}>
        <span>{rowLabel}</span>
        <span>{formatAsCurrency(Number(dueToday))}</span>
      </div>
      {hasPaymentOption ? (
        <div className={styles.totalPriceRow}>
          <span>Total price:</span>
          <span className={styles.totalPrice}>
            {formatAsCurrency(Number(totalAmount))}
          </span>
        </div>
      ) : null}
    </div>
  );
}
/* */

/*CheckoutBillingSummaryDetail */
function CheckoutBillingSummaryDetail({
  hotelLinks,
}: {
  hotelLinks: CartItemHotelLink[];
}) {
  const { cartItems } = useConsolidatedCheckout();
  function renderSummaryItems() {
    return cartItems?.map((cartItem: CombinedCartItemData, idx: number) => {
      return (
        <SummaryItem
          {...cartItem}
          key={`${cartItem.cartItemName}-${idx}`}
          hotelLink={hotelLinks[idx]?.hotelLink}
        />
      );
    });
  }
  return <div className={styles.summaryItems}>{renderSummaryItems()}</div>;
}
/* */

function SummaryItem({
  cartItemName,
  description,
  dueToday,
  hotelLink,
  lineItems,
  paymentTerm,
  selectedPaymentPlan,
  totalAmount,
}: CombinedCartItemData & { hotelLink?: string }) {
  function formatDiscountValue(discount: DiscountCode): string {
    return 'percentage' in discount && discount.percentage != null
      ? `${discount.percentage}%`
      : formatAsCurrency(Number(discount.fixedAmount));
  }

  function extractDiscountCodesFromLineItems(
    lineItems?: BillingSummaryItemLineItem[]
  ): string | null {
    // Early returns for invalid inputs
    if (!lineItems?.length || !lineItems[0]?.discountCodes?.length) {
      return null;
    }

    try {
      const firstDiscount = lineItems[0].discountCodes[0];
      const codeName = firstDiscount.code;
      const value = formatDiscountValue(firstDiscount);

      return `"${codeName}" - ${value} discount`;
    } catch (error) {
      console.error('Error processing discount code:', error);
      return null;
    }
  }

  const discountCodeLineItem = extractDiscountCodesFromLineItems(lineItems);

  return (
    <div className={styles.summaryItem}>
      <div className={styles.checkoutBillingSummaryPrimaryRow}>
        <span>{cartItemName}:</span>
        <PaymentsLineItem
          dueToday={dueToday}
          paymentTerm={paymentTerm}
          totalAmount={totalAmount}
        />
      </div>
      <div className={styles.checkoutBillingSummarySecondaryRow}>
        <span>{description}</span>
        <span>{selectedPaymentPlan?.name}</span>
      </div>
      {discountCodeLineItem ? (
        <div className={styles.checkoutBillingSummarySecondaryRow}>
          <span></span>
          <span>{discountCodeLineItem}</span>
        </div>
      ) : null}
      {hotelLink ? (
        <div className={styles.checkoutBillingSummaryTertiaryRow}>
          <Link href={hotelLink} isBold variant="text" target="_blank">
            Manage accommodations
            <ExternalLinkIcon
              fill="var(--secondary-600)"
              variant="bold"
              size="large"
            />
          </Link>
        </div>
      ) : null}
    </div>
  );
}

function PaymentsLineItem({
  dueToday,
  paymentTerm,
  totalAmount,
}: Readonly<{
  dueToday: string;
  paymentTerm?: string;
  totalAmount: string;
}>) {
  const dueTodayAmount =
    paymentTerm === 'PAYMENT_PLAN' && dueToday !== totalAmount ? (
      <>{formatAsCurrency(Number(dueToday))}/</>
    ) : null;
  return (
    <span>
      {dueTodayAmount}
      <span className={styles.totalCost}>
        {formatAsCurrency(Number(totalAmount))}
      </span>
    </span>
  );
}
